import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import { checkAHAVI, replaceHtmlToStr, showWarning } from 'utils/utils';
import {
  createDefaultHotelInfoConfig,
  getHotelInfoDescription,
  getHotelInfoFacilityText,
  getHotelInfoImageUrl,
  HotelInfoConfig,
} from 'utils/shared/hotelProposalInformation';
import { LoadingBlock } from '../../LoadingBlock';

interface ProposalHotelInformationProps {
  hotelData?: any;
  config?: HotelInfoConfig;
  onChangeConfig?: (config: HotelInfoConfig) => void;
  isEdit?: boolean;
}

const ProposalHotelInformation: FC<ProposalHotelInformationProps> = ({
  hotelData,
  config,
  onChangeConfig,
  isEdit,
}) => {
  const { t, i18n } = useTranslation();
  const currentConfig = config || createDefaultHotelInfoConfig(hotelData, i18n.language);
  const selectedLanguage = currentConfig.language;
  const imagesIndexes = currentConfig.imagesIndexes;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const languages = hotelData?.hotelInfoLangs?.map((lang: any) => lang.languageCode) || [];
  const description = getHotelInfoDescription(hotelData, selectedLanguage);
  const hasDetails =
    !!hotelData?.checkIn || !!hotelData?.checkOut || !!hotelData?.hotelFacilities?.length;

  useEffect(() => {
    if (!hotelData || config) {
      return;
    }

    onChangeConfig?.(createDefaultHotelInfoConfig(hotelData, i18n.language));
  }, [hotelData, config, i18n.language, onChangeConfig]);

  useEffect(() => {
    if (!languages.length || !config) {
      return;
    }

    if (!languages.includes(config.language)) {
      onChangeConfig?.({
        ...config,
        language: createDefaultHotelInfoConfig(hotelData, i18n.language).language,
      });
    }
  }, [hotelData, config, i18n.language, languages, onChangeConfig]);

  const setSelectedLanguage = (language: string) => {
    onChangeConfig?.({
      ...currentConfig,
      language,
      ...(isEdit
        ? {}
        : {
          imagesIndexes: [],
          details: undefined,
          overview: undefined,
        }),
    });
  };

  const onChangeChecked = (field: string, value: boolean | number[]) => {
    onChangeConfig?.({ ...currentConfig, [field]: value });
  };

  if (!hotelData) {
    return <LoadingBlock />;
  }

  return (
    <div className="tripadvisor-data">
      {languages.length > 1 && (
        <div className="flex-row mt-10 mb-10" dir="ltr">
          {languages.map((language: any) => (
            <div className="ph-10" key={language}>
              <button
                onClick={() => setSelectedLanguage(language)}
                className={
                  'btn btn-default btn-xsm' +
                  (selectedLanguage === language ? ' btn-active' : '')
                }
              >
                {language.toUpperCase()}
              </button>
            </div>
          ))}
        </div>
      )}

      <div
        className="hotel-result-settings-content"
        style={{ backgroundColor: 'transparent', height: 'calc(100% - 30px)' }}
      >
        <div className="text-sm">
          <div className="mb-10">{t('proposal.add_to')}</div>
          {hasDetails && (
            <Checkbox
              className="mb-5"
              checked={currentConfig.details}
              onChange={() => onChangeChecked('details', !currentConfig.details || false)}
              label={t('hotel.details')}
            />
          )}
          {!!description && (
            <Checkbox
              className="mb-20"
              checked={currentConfig.overview}
              onChange={() => onChangeChecked('overview', !currentConfig.overview || false)}
              label={t('hotel.overview')}
            />
          )}
        </div>
        <div className="details flex-column" style={{ width: '100%' }}>
          {hasDetails && (
            <div className="default-shadow details-block">
              <div>
                <div className="title-underline text-lg mb-20 title-header-grey">
                  {t('hotel.details')}
                </div>
                {!!hotelData.checkIn && (
                  <div className="mb-20 text-lh">
                    <div className="text-bold">{t('search.check_in')}</div>
                    <div
                      dangerouslySetInnerHTML={{ __html: replaceHtmlToStr(hotelData.checkIn) }}
                    />
                  </div>
                )}
                {!!hotelData.checkOut && (
                  <div className="mb-20 text-lh">
                    <div className="text-bold">{t('search.check_out')}</div>
                    <div
                      dangerouslySetInnerHTML={{ __html: replaceHtmlToStr(hotelData.checkOut) }}
                    />
                  </div>
                )}
                {!!hotelData.hotelFacilities?.length && (
                  <div className="text-lh pb-20">
                    <div className="title-underline text-lg mb-20">{t('hotel.facilities')}</div>
                    <div id="text-fac" className="row">
                      {hotelData.hotelFacilities.map((facility: any, index: number) => (
                        <div key={index} className="col-4">{getHotelInfoFacilityText(facility)}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {!!hotelData.images?.length && (
            <div className="default-shadow details-block">
              <div className="pr-photos-wr">
                {hotelData.images.map((image: any, index: number) => {
                  const isChecked = imagesIndexes.includes(index);
                  return (
                    <div key={index} className="h-photos position-relative">
                      <img
                        src={getHotelInfoImageUrl(image)}
                        alt="1"
                        onError={(e) => ((e.target as any).parentNode.style.display = 'none')}
                        style={{ pointerEvents: 'none' }}
                      />
                      <div className="pr-photos-checkbox">
                        <div
                          onClick={() => {
                            const newIndexes = isChecked
                              ? imagesIndexes.filter((i) => i !== index)
                              : [...imagesIndexes, index];
                            if (!isChecked && newIndexes.length > 3) {
                              showWarning(t('proposal.max_images_selected'));
                              return;
                            }
                            onChangeChecked('imagesIndexes', newIndexes);
                          }}
                          className={`image-checkbox ${isChecked ? 'selected' : ''}`}
                        >
                          {isChecked && (
                            <FontAwesomeIcon icon={faCheck} size="xs" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {!!description && (
            <div className="default-shadow details-block">
              <div className="title-underline title-header-grey text-lg mb-20">
                {t('hotel.overview')}
              </div>
              <div
                className={'text-lh' + (checkAHAVI(description) ? ' rtl' : ' ltr')}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProposalHotelInformation;
